// src/products/analysis/utils/correlationUtils.js

/**
 * Calculate Pearson correlation coefficient between two arrays of values
 * @param {Array<number>} x - First array of values
 * @param {Array<number>} y - Second array of values
 * @returns {number} - Correlation coefficient (-1 to 1)
 */
export const calculateCorrelation = (x, y) => {
  if (!x || !y || x.length !== y.length || x.length === 0) {
    return null;
  }

  // Filter out null/undefined values and ensure arrays have same length
  const validPairs = [];
  for (let i = 0; i < x.length; i++) {
    if (x[i] !== null && x[i] !== undefined && 
        y[i] !== null && y[i] !== undefined &&
        !isNaN(x[i]) && !isNaN(y[i])) {
      validPairs.push({ x: x[i], y: y[i] });
    }
  }

  if (validPairs.length < 2) {
    return null;
  }

  const n = validPairs.length;
  const xValues = validPairs.map(p => p.x);
  const yValues = validPairs.map(p => p.y);

  // Calculate means
  const meanX = xValues.reduce((sum, val) => sum + val, 0) / n;
  const meanY = yValues.reduce((sum, val) => sum + val, 0) / n;

  // Calculate numerator: sum of (x - meanX) * (y - meanY)
  let numerator = 0;
  for (let i = 0; i < n; i++) {
    numerator += (xValues[i] - meanX) * (yValues[i] - meanY);
  }

  // Calculate denominators: sqrt of sum of squares
  const sumSqX = xValues.reduce((sum, val) => sum + Math.pow(val - meanX, 2), 0);
  const sumSqY = yValues.reduce((sum, val) => sum + Math.pow(val - meanY, 2), 0);

  const denominator = Math.sqrt(sumSqX * sumSqY);

  if (denominator === 0) {
    return null;
  }

  return numerator / denominator;
};

/**
 * Calculate correlation for time series data
 * Aligns data by timestamp and calculates correlation
 * @param {Array} data1 - First time series [{timestamp, close}, ...]
 * @param {Array} data2 - Second time series [{timestamp, close}, ...]
 * @returns {Object} - { correlation: number, alignedData: Array, dataPoints: number }
 */
export const calculateTimeSeriesCorrelation = (data1, data2) => {
  if (!data1 || !data2 || data1.length === 0 || data2.length === 0) {
    return { correlation: null, alignedData: [], dataPoints: 0 };
  }

  // Create maps for quick lookup
  const map1 = new Map(data1.map(d => [d.timestamp, d.close]));
  const map2 = new Map(data2.map(d => [d.timestamp, d.close]));

  // Find common timestamps
  const timestamps1 = new Set(data1.map(d => d.timestamp));
  const timestamps2 = new Set(data2.map(d => d.timestamp));
  const commonTimestamps = [...timestamps1].filter(ts => timestamps2.has(ts));

  if (commonTimestamps.length < 2) {
    return { correlation: null, alignedData: [], dataPoints: 0 };
  }

  // Sort timestamps
  commonTimestamps.sort((a, b) => a - b);

  // Extract aligned values
  const alignedData = commonTimestamps.map(timestamp => ({
    timestamp,
    value1: map1.get(timestamp),
    value2: map2.get(timestamp)
  }));

  const x = alignedData.map(d => d.value1);
  const y = alignedData.map(d => d.value2);

  const correlation = calculateCorrelation(x, y);

  return {
    correlation,
    alignedData,
    dataPoints: commonTimestamps.length
  };
};

/**
 * Interpret correlation coefficient
 * @param {number} correlation - Correlation coefficient
 * @returns {Object} - { strength: string, direction: string, description: string }
 */
export const interpretCorrelation = (correlation) => {
  if (correlation === null || isNaN(correlation)) {
    return {
      strength: 'Unknown',
      direction: 'Unknown',
      description: 'Insufficient data to calculate correlation'
    };
  }

  const abs = Math.abs(correlation);
  let strength = '';
  let direction = correlation >= 0 ? 'Positive' : 'Negative';

  if (abs >= 0.9) {
    strength = 'Very Strong';
  } else if (abs >= 0.7) {
    strength = 'Strong';
  } else if (abs >= 0.5) {
    strength = 'Moderate';
  } else if (abs >= 0.3) {
    strength = 'Weak';
  } else {
    strength = 'Very Weak';
  }

  let description = '';
  if (direction === 'Positive') {
    if (strength === 'Very Strong' || strength === 'Strong') {
      description = `${strength} positive correlation: These assets tend to move together.`;
    } else if (strength === 'Moderate') {
      description = `${strength} positive correlation: Some tendency to move together.`;
    } else {
      description = `${strength} positive correlation: Limited relationship.`;
    }
  } else {
    if (strength === 'Very Strong' || strength === 'Strong') {
      description = `${strength} negative correlation: These assets tend to move in opposite directions.`;
    } else if (strength === 'Moderate') {
      description = `${strength} negative correlation: Some tendency to move in opposite directions.`;
    } else {
      description = `${strength} negative correlation: Limited relationship.`;
    }
  }

  return { strength, direction, description };
};



